import { JxtEventFormRange, JxtInput } from '@jooxter/ui';
import { UseFormRegister, FieldValues, FormState, UseFormGetFieldState } from 'react-hook-form';
import { z, ZodTypeAny } from 'zod';
import { useTranslation } from 'react-i18next';
import { FloorCompressed, FloorExceptionalClosingHoursResponse, LocationCompressed } from '@jooxter/api';
import { useMemo } from 'react';
import { Settings } from 'luxon';
import { useFetchUser } from '../../../queries';

export const useGenerateFloorExceptionalClosingHoursForm = (
  {
    register,
    formState,
    getFieldState,
  }: {
    register: UseFormRegister<FieldValues>;
    formState: FormState<FieldValues>;
    getFieldState: UseFormGetFieldState<FieldValues>;
  },
  {
    schema,
    floorExceptionalClosingHoursToEdit,
    floor,
  }: {
    schema?: z.ZodObject<
      {
        [x: string]: ZodTypeAny;
      },
      'strip',
      ZodTypeAny,
      {
        [x: string]: unknown;
      },
      {
        [x: string]: unknown;
      }
    >;
    floorExceptionalClosingHoursToEdit?: FloorExceptionalClosingHoursResponse | null;
    floor?: FloorCompressed | null;
  },
  selectedBuilding: LocationCompressed
) => {
  const { t } = useTranslation();
  const { user } = useFetchUser();

  const floorTimezone = useMemo(() => {
    if (floor) {
      return selectedBuilding.timezone;
    }
    return Settings.defaultZone.name;
  }, [floor, selectedBuilding.timezone]);

  if (!schema?.shape || !user?.id) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-[min-content_auto] items-center gap-x-3">
        <i className="fas fa-fw fa-lg fa-quote-left text-neutral-60 col-start-1 row-start-1" />
        <div className="col-start-2 row-start-1">
          <JxtInput
            name="title"
            placeholder={t<string>('manage-space-form.summary-input-placeholder')}
            register={register}
            formState={formState}
            getFieldState={getFieldState}
            showErrorMessages={false}
          />
        </div>
        {getFieldState('title').error?.message && typeof getFieldState('title').error?.message === 'string' && (
          <p className="text-red-100 pt-1 col-start-2 row-start-2">{getFieldState('title').error?.message as string}</p>
        )}
      </div>
      <JxtEventFormRange
        sameDay={false}
        timezone={floorTimezone}
        isEditing={!!floorExceptionalClosingHoursToEdit}
        formScope={'floor'}
      />
    </>
  );
};
